import { getOfficialAssetMaterialInfo } from '$/api/canvasRquest';
import type {
  OfficialAssetServerImage,
  OfficialAssetServerMaterial,
} from '$/api/server.types';
import { assetDisplayTitle } from '$/utils/assetLibraryCardLayout';
import type { AssetModalListItem } from '../../type';
import { mergeOfficialAssetImagesAndUpload } from '../../utils';
import {
  INITIAL_ROLE_GEN_FORM_VALUES,
  type RoleGenFormValues,
  type RoleGenMaterialCategory,
  type RoleGenMaterialFilter,
  type RoleGenMaterialSource,
} from './roleFormTypes';
import type { RoleGenNodeExtra } from './roleGenerateTask';

interface RoleMaterialPayload {
  id: string;
  name: string;
  coverUrl: string;
  source?: RoleGenMaterialSource;
  officialMaterial?: OfficialAssetServerMaterial;
  filter?: RoleGenMaterialFilter;
}

export interface RoleGenSubmitValues {
  displayValues: RoleGenFormValues;
  submitValues: RoleGenFormValues;
}

export const ROLE_GEN_MATERIAL_CATEGORIES: RoleGenMaterialCategory[] = ['clothing', 'hairstyle', 'accessory'];

const ROLE_GEN_OFFICIAL_MATERIAL_IMAGE_KEYS = [
  'main_image',
  'detail_image',
  'four_view_image',
  'wearing_image',
  'nine_grid_image',
  'three_view_image',
  'top_view_image',
  'panorama_image',
] as const;

const officialMaterialDetailRequestCache = new Map<
number,
Promise<OfficialAssetServerMaterial | undefined>
>();

/** 按官方资产字段顺序读取当前素材的全部有效图片，供合并和放大预览共用。 */
export function getRoleGenOfficialMaterialImages(
  material: OfficialAssetServerMaterial,
): OfficialAssetServerImage[] {
  return ROLE_GEN_OFFICIAL_MATERIAL_IMAGE_KEYS
    .map((key) => material[key])
    .filter((image): image is OfficialAssetServerImage => Boolean(image?.url));
}

/** 创建一份独立的角色表单初始值，避免 reset 时复用嵌套对象引用。 */
export function createInitialRoleGenValues(): RoleGenFormValues {
  return {
    ...INITIAL_ROLE_GEN_FORM_VALUES,
    modelParameters: { ...INITIAL_ROLE_GEN_FORM_VALUES.modelParameters },
  };
}

/** 将 node_extra 中的官方素材筛选条件转换为表单可直接恢复的结构。 */
function buildMaterialFilterSnapshot(
  filter?: RoleGenNodeExtra['role_gen']['materials']['clothing']['filter'],
): RoleGenFormValues['clothingMaterialFilter'] {
  if (!filter) return undefined;
  return {
    genreId: filter.genre_id,
    tags: Object.fromEntries(
      Object.entries(filter.tags).map(([key, values]) => [key, [...values]]),
    ),
    keyword: filter.keyword,
  };
}

/** 将持久化的 role_gen 参数转换为创建角色弹框可回填的完整表单值。 */
export function buildRoleGenFormSnapshot(nodeExtra: RoleGenNodeExtra): RoleGenFormValues {
  const roleGen = nodeExtra.role_gen;
  return {
    ...createInitialRoleGenValues(),
    name: roleGen.name,
    prompt: roleGen.prompt,
    referenceImageUrl: roleGen.reference_image_url,
    clothingMaterialId: roleGen.materials.clothing.id || '',
    clothingMaterialName: roleGen.materials.clothing.name,
    clothingMaterialCoverUrl: roleGen.materials.clothing.cover_url,
    clothingMaterialFilter: buildMaterialFilterSnapshot(roleGen.materials.clothing.filter),
    hairstyleMaterialId: roleGen.materials.hairstyle.id,
    hairstyleMaterialName: roleGen.materials.hairstyle.name,
    hairstyleMaterialCoverUrl: roleGen.materials.hairstyle.cover_url,
    hairstyleMaterialFilter: buildMaterialFilterSnapshot(roleGen.materials.hairstyle.filter),
    accessoryMaterialId: roleGen.materials.accessory.id,
    accessoryMaterialName: roleGen.materials.accessory.name,
    accessoryMaterialCoverUrl: roleGen.materials.accessory.cover_url,
    accessoryMaterialFilter: buildMaterialFilterSnapshot(roleGen.materials.accessory.filter),
    modelId: roleGen.model.model_id,
    modelApiName: roleGen.model.api_name,
    nodeModelId: roleGen.model.node_model_id,
    modelParameters: roleGen.model.parameters,
  };
}

/**
 * 根据素材类型生成对应表单字段补丁，避免官方素材、本地上传和历史记录回填逻辑分叉。
 *
 * @param category 当前素材行类型
 * @param payload 统一后的素材展示与提交字段
 * @returns 可直接合并进表单值的补丁
 */
export function buildMaterialValuePatch(
  category: RoleGenMaterialCategory,
  payload: RoleMaterialPayload,
): Partial<RoleGenFormValues> {
  if (category === 'clothing') {
    return {
      clothingMaterialId: payload.id,
      clothingMaterialName: payload.name,
      clothingMaterialCoverUrl: payload.coverUrl,
      clothingMaterialSource: payload.source,
      clothingOfficialMaterial: payload.officialMaterial,
      clothingMaterialFilter: payload.filter,
    };
  }
  if (category === 'hairstyle') {
    return {
      hairstyleMaterialId: payload.id,
      hairstyleMaterialName: payload.name,
      hairstyleMaterialCoverUrl: payload.coverUrl,
      hairstyleMaterialSource: payload.source,
      hairstyleOfficialMaterial: payload.officialMaterial,
      hairstyleMaterialFilter: payload.filter,
    };
  }
  return {
    accessoryMaterialId: payload.id,
    accessoryMaterialName: payload.name,
    accessoryMaterialCoverUrl: payload.coverUrl,
    accessoryMaterialSource: payload.source,
    accessoryOfficialMaterial: payload.officialMaterial,
    accessoryMaterialFilter: payload.filter,
  };
}

/** 读取指定素材行的官方素材查询上下文，筛选条件存在时才视为官方素材。 */
function getOfficialMaterialLookup(
  values: RoleGenFormValues,
  category: RoleGenMaterialCategory,
): {
    id?: string;
    filter?: RoleGenMaterialFilter;
    material?: OfficialAssetServerMaterial;
  } {
  if (category === 'clothing') {
    return {
      id: values.clothingMaterialId,
      filter: values.clothingMaterialFilter,
      material: values.clothingOfficialMaterial,
    };
  }
  if (category === 'hairstyle') {
    return {
      id: values.hairstyleMaterialId,
      filter: values.hairstyleMaterialFilter,
      material: values.hairstyleOfficialMaterial,
    };
  }
  return {
    id: values.accessoryMaterialId,
    filter: values.accessoryMaterialFilter,
    material: values.accessoryOfficialMaterial,
  };
}

/** 按素材 id 获取官方素材详情并复用并发请求，请求失败时移除缓存以允许下次重试。 */
async function loadOfficialMaterialDetail(
  id: number,
): Promise<OfficialAssetServerMaterial | undefined> {
  const cachedRequest = officialMaterialDetailRequestCache.get(id);
  if (cachedRequest) return cachedRequest;

  const request = getOfficialAssetMaterialInfo(id)
    .then((data) => data.info)
    .catch(() => {
      officialMaterialDetailRequestCache.delete(id);
      return undefined;
    });
  officialMaterialDetailRequestCache.set(id, request);
  return request;
}

/** 根据 node_extra 保存的官方素材 id 补全名称、封面和多图详情。 */
async function loadOfficialMaterialPatch(
  values: RoleGenFormValues,
  category: RoleGenMaterialCategory,
): Promise<Partial<RoleGenFormValues>> {
  const lookup = getOfficialMaterialLookup(values, category);
  if (lookup.material || !lookup.filter || !lookup.id) return {};

  const materialId = Number(lookup.id);
  if (!Number.isInteger(materialId) || materialId <= 0) return {};
  const material = await loadOfficialMaterialDetail(materialId);
  if (!material) return {};

  return buildMaterialValuePatch(category, {
    id: String(material.id),
    name: material.name,
    coverUrl: material.main_image.url,
    source: 'official',
    officialMaterial: material,
    filter: lookup.filter,
  });
}

/** 并发补全表单内指定类别的官方素材详情，仅返回素材字段补丁。 */
export async function loadRoleGenOfficialMaterialPatch(
  values: RoleGenFormValues,
  categories: RoleGenMaterialCategory[] = ROLE_GEN_MATERIAL_CATEGORIES,
): Promise<Partial<RoleGenFormValues>> {
  const patches = await Promise.all(
    categories.map((category) => loadOfficialMaterialPatch(values, category)),
  );
  return Object.assign({}, ...patches);
}

/**
 * 将历史记录列表项转换为创建角色表单展示所需的素材字段。
 *
 * @param item 历史记录选择项
 * @returns 当前素材行可消费的字段
 */
export function historyItemToRoleMaterialPayload(item: AssetModalListItem): RoleMaterialPayload {
  const coverUrl = item.coverUrl || item.mediaUrl;
  return {
    id: item.id,
    name: assetDisplayTitle(item.raw, item.mediaUrl || coverUrl, item.id),
    coverUrl,
    source: 'history',
  };
}

/** 读取当前素材行保存的官方素材原始数据，仅官方资产选择来源需要提交前合并。 */
function getOfficialMaterialForCategory(
  values: RoleGenFormValues,
  category: RoleGenMaterialCategory,
): OfficialAssetServerMaterial | undefined {
  if (category === 'clothing') {
    return values.clothingMaterialSource === 'official' ? values.clothingOfficialMaterial : undefined;
  }
  if (category === 'hairstyle') {
    return values.hairstyleMaterialSource === 'official' ? values.hairstyleOfficialMaterial : undefined;
  }
  return values.accessoryMaterialSource === 'official' ? values.accessoryOfficialMaterial : undefined;
}

/** 获取素材行当前展示名，合并后继续保留给历史记录回显。 */
function getMaterialNameForCategory(values: RoleGenFormValues, category: RoleGenMaterialCategory): string {
  if (category === 'clothing') return values.clothingMaterialName || '';
  if (category === 'hairstyle') return values.hairstyleMaterialName || '';
  return values.accessoryMaterialName || '';
}

/** 获取素材行当前原始 id，官方素材合并后仍用它做回显标识。 */
function getMaterialIdForCategory(values: RoleGenFormValues, category: RoleGenMaterialCategory): string {
  if (category === 'clothing') return values.clothingMaterialId || '';
  if (category === 'hairstyle') return values.hairstyleMaterialId || '';
  return values.accessoryMaterialId || '';
}

/** 统计官方素材有效图片数量，单图素材不需要走画布合并上传。 */
function getOfficialMaterialImageCount(material: OfficialAssetServerMaterial): number {
  return getRoleGenOfficialMaterialImages(material).length;
}

/** 提交前生成接口专用素材图，UI 和历史回显继续保留官方素材主图。 */
export async function prepareRoleGenSubmitValues(values: RoleGenFormValues): Promise<RoleGenSubmitValues> {
  const mergeTasks = ROLE_GEN_MATERIAL_CATEGORIES.reduce<Array<{
    category: RoleGenMaterialCategory;
    officialMaterial: OfficialAssetServerMaterial;
  }>>((tasks, category) => {
    const officialMaterial = getOfficialMaterialForCategory(values, category);
    if (!officialMaterial || getOfficialMaterialImageCount(officialMaterial) <= 1) {
      return tasks;
    }
    return [...tasks, { category, officialMaterial }];
  }, []);

  const mergedMaterials = await Promise.all(mergeTasks.map(async ({ category, officialMaterial }) => ({
    category,
    officialMaterial,
    mergedImage: await mergeOfficialAssetImagesAndUpload(officialMaterial),
  })));

  const submitValues = mergedMaterials.reduce<RoleGenFormValues>((nextValues, {
    category, officialMaterial, mergedImage,
  }) => ({
    ...nextValues,
    ...buildMaterialValuePatch(category, {
      id: getMaterialIdForCategory(values, category) || String(officialMaterial.id),
      name: getMaterialNameForCategory(values, category) || officialMaterial.name,
      coverUrl: mergedImage.url,
      source: 'upload',
    }),
  }), values);

  return {
    displayValues: values,
    submitValues,
  };
}
